import type { ScreenerFilter, ScreenerFilters } from '@api/api/screener';
import { Icon } from '@design/components/icon/Icon';
import * as Radix from '@radix-ui/react-dropdown-menu';
import filterStyles from '@terminal/components/filter/Filter.module.scss';
import { FilterSearch } from '@terminal/views/screener/filter-search/FilterSearch';
import { ChangeEvent, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddMoreFilterOption } from './AddMoreFilterOption';

type FilterProps = {
  filterGroups: [string, ScreenerFilters][];
  activeFilters: ScreenerFilter[];
  handleClick: (filterName: string, isChecked: boolean) => void;
  searchPlaceholder?: string;
  noResultsLabel: string;
};

export const AddMoreFilters = ({
  filterGroups,
  activeFilters,
  handleClick,
  searchPlaceholder,
  noResultsLabel,
}: FilterProps) => {
  const [value, setValue] = useState('');
  const { t } = useTranslation(['screener']);

  const onClear = () => {
    setValue('');
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleSelect = (filterKey: string, isChecked: boolean) => {
    handleClick(filterKey, isChecked);
    onClear();
  };

  const mappedGroups = filterGroups
    .map(([category, options]) => {
      const filteredOptions = options.filter(o =>
        o.name.toLowerCase().includes(value.toLowerCase())
      );
      return (
        !!filteredOptions.length && (
          <Fragment key={category}>
            <p className={filterStyles.menuTitleText}>{category}</p>
            {filteredOptions.map(option => {
              const isChecked = !!activeFilters.find(f => f.key === option.key);

              return (
                <AddMoreFilterOption
                  key={option.key}
                  filterKey={option.key}
                  name={option.name}
                  locked={option.locked}
                  isChecked={isChecked}
                  handleClick={filterKey => handleSelect(filterKey, isChecked)}
                />
              );
            })}
          </Fragment>
        )
      );
    })
    .filter(Boolean);
  const showEmpty = !mappedGroups.length;

  return (
    <Radix.Root>
      <Radix.Trigger className={filterStyles.dashedTrigger}>
        <span className={filterStyles.triggerLabel} data-trigger-label>
          {t('screener:addMore')}
        </span>
        <Icon iconName="Add" size={14} fillColor="var(--icon-soft)" />
      </Radix.Trigger>
      <Radix.Portal>
        <Radix.Content
          className={filterStyles.dropdownMenuContent}
          sideOffset={5}
          align="start"
          onInteractOutside={onClear}
        >
          <FilterSearch
            value={value}
            searchPlaceholder={searchPlaceholder}
            onChange={onSearch}
            onClear={onClear}
          />
          <div className={filterStyles.separator} />
          <div className={filterStyles.scrollableList}>
            {showEmpty ? (
              <p className={filterStyles.noResults}>{noResultsLabel}</p>
            ) : (
              mappedGroups
            )}
          </div>
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  );
};
